import React, { useEffect, useState } from 'react';
import Layout, { NAVBAR_ISMINIMIZED_KEY } from '../../components/Layout/Layout.component';
import { useStyles } from '../styles';
import Breadcrumbs from '../../components/Breadcrumbs.component';
import { ButtonOne } from '../../components/ButtonOne/ButtonOne.component';
import { AxiosResponse } from 'axios';
import { getFaqList, deleteFaq } from '../../services/support/faq/supportFaq.service';

import { IFaqListResponse, IFaqListItem } from '../../services/support/faq/supportFaq.types';

import { AddFaq } from '../../components/Support/AddFaq/AddFaq.component';
import { EditFaq } from '../../components/Support/EditFaq/EditFaq.component';
import { FaqDetails } from '../../components/Support/FaqDetails/FaqDetails.component';
import usePermission, { UserPermission } from '../../hooks/usePermission';
import { Prompt } from '../../components/Prompt/Prompt.component';
import { useLayoutContext } from '../../Context/Layout.context';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import { useTranslation } from 'react-i18next';
import { Paths } from '../../utils/routerUrls';
import { Typography } from '@material-ui/core';

const SupportFaq: React.FC = () => {
  const [isAddFaqOpen, setIsAddFaqOpen] = useState<boolean>(false);
  const [faqs, setFaqs] = useState<IFaqListItem[]>([]);

  const [deletingFaqPromptData, setDeletingFaqPromptData] = useState<{ faqId: string; question: string } | undefined>(
    undefined,
  );
  const [faqDataToEdit, setFaqDataToEdit] = useState<IFaqListItem | undefined>(undefined);

  const classes = useStyles();

  const isAdminPermission = usePermission(UserPermission.AdminSupportManage);
  const { openAlert } = useLayoutContext();
  const { t } = useTranslation();
  useDocumentTitle('FAQ');

  const refreshData = () => {
    getFaqList()
      .then((response: AxiosResponse<IFaqListResponse>) => {
        setFaqs(response.data.list);
      })
      .catch(() => {
        console.error('error');
      });
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleDeleteFaq = (faqId: string) => {
    deleteFaq(faqId)
      .then((response: AxiosResponse<boolean>) => {
        if (response.data) {
          openAlert('success', 'Usunięcie pytania zakończone sukcesem');
          refreshData();
        } else {
          openAlert('error', `Usunięcie pytania nie powiodło się`);
        }
      })
      .finally(() => {
        setDeletingFaqPromptData(undefined);
      });
  };

  return (
    <>
      <Layout viewType={isAdminPermission ? 'admin' : 'fullwidthForExaminer'}>
        <div
          className={classes.tableAndBreadcrumContainer}
          style={{
            overflow: 'scroll',
          }}
        >
          <div className={classes.breadcrumbHeader}>
            <Breadcrumbs
              listRoute={Paths.supportFaq}
              currentList={'FAQ'}
              mainElementRoute={Paths.support}
              mainElement={`Centrum pomocy ${t('global:shortNames.beneficiary')} ${t('global:shortNames.system')}`}
            />
            {isAdminPermission && (
              <div className={classes.buttonsContainer}>
                <ButtonOne
                  className={classes.primary}
                  variant="contained"
                  aria-label="Dodaj pytanie"
                  onClick={() => setIsAddFaqOpen(true)}
                >
                  <span style={{ margin: '0px 10px 0px -4px', fontSize: '20px' }}>+</span> {t('support:header.addFaq')}
                </ButtonOne>
              </div>
            )}
          </div>

          <Typography variant="h1" className={classes.gridHeader} style={{ margin: '0px 30px 20px 30px' }}>
            FAQ
          </Typography>

          <div style={{ margin: '0px auto' }}>
            {faqs.map((faq) => (
              <div key={faq.id}>
                <FaqDetails
                  faqProp={faq}
                  deleteFunc={() => setDeletingFaqPromptData({ faqId: faq.id, question: faq.question })}
                  updateFunc={() => setFaqDataToEdit(faq)}
                  isAdminPermission={isAdminPermission}
                />
              </div>
            ))}
          </div>

          {isAddFaqOpen && <AddFaq onClose={() => setIsAddFaqOpen(false)} onChange={refreshData} />}

          {faqDataToEdit && (
            <EditFaq onClose={() => setFaqDataToEdit(undefined)} onChange={refreshData} faqProp={faqDataToEdit} />
          )}

          {deletingFaqPromptData && (
            <Prompt
              type="confirmation"
              open={true}
              size="xs"
              onConfirm={() => handleDeleteFaq(deletingFaqPromptData.faqId)}
              onCancel={() => setDeletingFaqPromptData(undefined)}
              mainActionButtonText="TAK"
              mainText="Czy na pewno chcesz usunąć pytanie:"
              subTexts={[{ text: deletingFaqPromptData.question }]}
            />
          )}
        </div>
      </Layout>
    </>
  );
};

export default SupportFaq;
